import { useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { QueryState } from "@components/ui/QueryState";
import { Modal } from "@components/ui/Modal";
import { PicklistSelect } from "@components/ui/PicklistSelect";
import { fmtDepth } from "@lib/format";
import type { BitOpOut } from "@api/types";
import type { Locale } from "@i18n/locales";
import {
  useGetBhaRunsQuery,
  useCreateBhaRunMutation,
  useUpdateBhaRunMutation,
  type BhaRunWithEntity,
  type BhaRunCreate,
  type BhaRunUpdate,
  type BitOpCreate,
} from "./bhaApi";
import "./bha.css";

/**
 * Editable BHA panel — lists the event's BHA runs (date in/out, MD in/out, and
 * their bit-operation lines) with per-run Edit (PUT) and a "+ Agregar" add form
 * (POST). Adding bit-ops to a run goes through PUT, which APPENDS them. The runs
 * list refetches on every successful write (tag invalidation in bhaApi).
 *
 * BHA is event-scoped and applies to ALL event types, so there is no
 * drilling-only 422 gate here.
 */
export function BhaEditPanel({ eventId, wellUwi }: { eventId: string; wellUwi: string }) {
  const { t, locale } = useLocale();
  const { data, isLoading, isError, refetch } = useGetBhaRunsQuery(eventId, {
    skip: !eventId,
  });
  const [adding, setAdding] = useState(false);

  const runs = Array.isArray(data?.runs) ? data!.runs : [];

  return (
    // Gate only on load/error — never on emptiness, so the "+ Agregar" control
    // stays reachable when the event has no runs yet.
    <QueryState isLoading={isLoading} isError={isError} onRetry={refetch}>
      <div className="bha-edit">
        <div className="panel-head-row" style={{ marginBottom: 12 }}>
          <p className="subtitle">{t("bhaEdit.subtitle")}</p>
          <button
            type="button"
            className="brand-check"
            onClick={() => setAdding((v) => !v)}
            aria-expanded={adding}
          >
            {adding ? t("bhaEdit.cancel") : t("bhaEdit.addRun")}
          </button>
        </div>

        {adding ? (
          <RunForm
            eventId={eventId}
            wellUwi={wellUwi}
            locale={locale}
            onDone={() => setAdding(false)}
          />
        ) : null}

        {runs.length === 0 && !adding ? (
          <div className="empty-state">{t("bhaEdit.noRuns")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {runs.map((run, i) => (
              <EditableRunCard
                key={run.id}
                run={run}
                index={i}
                eventId={eventId}
                wellUwi={wellUwi}
                locale={locale}
              />
            ))}
          </div>
        )}
      </div>
    </QueryState>
  );
}

/** A run card with an Edit affordance (opens the run form pre-filled, PUT) and
 *  a "+ bit-op" affordance (opens a bit-op form; PUT appends it to the run). */
function EditableRunCard({
  run,
  index,
  eventId,
  wellUwi,
  locale,
}: {
  run: BhaRunWithEntity;
  index: number;
  eventId: string;
  wellUwi: string;
  locale: Locale;
}) {
  const { t } = useLocale();
  const [editing, setEditing] = useState(false);
  const [addingBitOp, setAddingBitOp] = useState(false);
  const bitOps = Array.isArray(run.bit_ops) ? run.bit_ops : [];
  const title = run.assembly?.assembly_name ?? `${t("bha.run")} ${index + 1}`;

  return (
    <div className="card" data-owner="p">
      <div className="panel-head-row">
        <h3>{title}</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="brand-check" onClick={() => setEditing((v) => !v)}>
            {editing ? t("bhaEdit.cancel") : t("bhaEdit.edit")}
          </button>
          <button
            type="button"
            className="brand-check"
            onClick={() => setAddingBitOp((v) => !v)}
          >
            {addingBitOp ? t("bhaEdit.cancel") : t("bhaEdit.addBitOp")}
          </button>
        </div>
      </div>

      <div className="kv-grid">
        <KV
          k={t("bha.run.dates")}
          v={`${fmtDate(run.date_in, locale)} / ${fmtDate(run.date_out, locale)}`}
        />
        <KV
          k={t("bha.run.mdSpan")}
          v={`${fmtDepth(run.md_in, locale)} / ${fmtDepth(run.md_out, locale)}`}
          mono
        />
      </div>

      {editing ? (
        <RunForm
          eventId={eventId}
          wellUwi={wellUwi}
          locale={locale}
          initial={run}
          onDone={() => setEditing(false)}
        />
      ) : null}

      {addingBitOp ? (
        <BitOpForm
          eventId={eventId}
          wellUwi={wellUwi}
          runEntityId={run.entity_id}
          locale={locale}
          onDone={() => setAddingBitOp(false)}
        />
      ) : null}

      <h4 style={{ marginTop: 14 }}>{t("bha.bitOps")}</h4>
      {bitOps.length === 0 ? (
        <div className="empty-state">{t("bha.bitOps.empty")}</div>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("bha.col.date")}</th>
                <th className="num">{t("bha.col.md")}</th>
                <th className="num">{t("bha.col.duration")}</th>
                <th className="num">{t("bha.col.progress")}</th>
                <th className="num">{t("bha.col.rop")}</th>
                <th>{t("bha.col.code")}</th>
              </tr>
            </thead>
            <tbody>
              {bitOps.map((op) => (
                <BitOpRow key={op.id} op={op} locale={locale} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Add/edit form for a run. On add (no `initial`) POSTs; on edit PUTs keyed on
 *  entity_id. Numeric/date fields submit only when filled. */
function RunForm({
  eventId,
  wellUwi,
  locale,
  initial,
  onDone,
}: {
  eventId: string;
  wellUwi: string;
  locale: Locale;
  initial?: BhaRunWithEntity;
  onDone: () => void;
}) {
  const { t } = useLocale();
  const [createRun, createState] = useCreateBhaRunMutation();
  const [updateRun, updateState] = useUpdateBhaRunMutation();
  const busy = createState.isLoading || updateState.isLoading;
  const err = createState.error ?? updateState.error;

  const [dateIn, setDateIn] = useState(toDateInput(initial?.date_in));
  const [dateOut, setDateOut] = useState(toDateInput(initial?.date_out));
  const [mdIn, setMdIn] = useState(numToStr(initial?.md_in));
  const [mdOut, setMdOut] = useState(numToStr(initial?.md_out));

  async function submit() {
    const base = {
      date_in: nullIfBlank(dateIn),
      date_out: nullIfBlank(dateOut),
      md_in: strToNum(mdIn),
      md_out: strToNum(mdOut),
    };
    try {
      if (initial) {
        const body: BhaRunUpdate = { ...base };
        await updateRun({
          eventId,
          entityId: initial.entity_id,
          wellUwi,
          body,
        }).unwrap();
      } else {
        const body: BhaRunCreate = { well_uwi: wellUwi, ...base };
        await createRun({ eventId, body }).unwrap();
      }
      onDone();
    } catch {
      /* error surfaced from mutation state below */
    }
  }

  return (
    <Modal
      open
      title={initial ? t("bhaEdit.edit") : t("bhaEdit.addRun")}
      onClose={onDone}
      onConfirm={submit}
      busy={busy}
      confirmLabel={busy ? t("bhaEdit.saving") : t("bhaEdit.save")}
      cancelLabel={t("bhaEdit.cancel")}
      footerNote={err ? errorMessage(err, t, locale) : null}
    >
      <div className="form-grid">
        <Field label={t("bhaEdit.field.dateIn")}>
          <input type="date" value={dateIn} onChange={(e) => setDateIn(e.target.value)} />
        </Field>
        <Field label={t("bhaEdit.field.dateOut")}>
          <input type="date" value={dateOut} onChange={(e) => setDateOut(e.target.value)} />
        </Field>
        <Field label={t("bhaEdit.field.mdIn")}>
          <input
            type="number"
            step="any"
            inputMode="decimal"
            value={mdIn}
            onChange={(e) => setMdIn(e.target.value)}
          />
        </Field>
        <Field label={t("bhaEdit.field.mdOut")}>
          <input
            type="number"
            step="any"
            inputMode="decimal"
            value={mdOut}
            onChange={(e) => setMdOut(e.target.value)}
          />
        </Field>
      </div>
    </Modal>
  );
}

/** Bit-op add form. Submits a single BitOpCreate via PUT on the run (APPEND). */
function BitOpForm({
  eventId,
  wellUwi,
  runEntityId,
  locale,
  onDone,
}: {
  eventId: string;
  wellUwi: string;
  runEntityId: string;
  locale: Locale;
  onDone: () => void;
}) {
  const { t } = useLocale();
  const [updateRun, updateState] = useUpdateBhaRunMutation();

  const [dateOp, setDateOp] = useState("");
  const [mdOp, setMdOp] = useState("");
  const [duration, setDuration] = useState("");
  const [progress, setProgress] = useState("");
  const [rop, setRop] = useState("");
  const [codeKey, setCodeKey] = useState("");

  async function submit() {
    const op: BitOpCreate = {
      date_op: nullIfBlank(dateOp),
      md_op: strToNum(mdOp),
      op_duration: strToNum(duration),
      op_progress: strToNum(progress),
      rop: strToNum(rop),
      activity_code_entry_key: nullIfBlank(codeKey),
    };
    try {
      // PUT with bit_ops APPENDS to the run's existing bit-ops.
      await updateRun({
        eventId,
        entityId: runEntityId,
        wellUwi,
        body: { bit_ops: [op] },
      }).unwrap();
      onDone();
    } catch {
      /* surfaced below */
    }
  }

  return (
    <Modal
      open
      title={t("bhaEdit.addBitOp")}
      onClose={onDone}
      onConfirm={submit}
      busy={updateState.isLoading}
      confirmLabel={updateState.isLoading ? t("bhaEdit.saving") : t("bhaEdit.save")}
      cancelLabel={t("bhaEdit.cancel")}
      footerNote={
        updateState.error ? errorMessage(updateState.error, t, locale) : null
      }
    >
      <div className="form-grid">
        <Field label={t("bha.col.date")}>
          <input type="date" value={dateOp} onChange={(e) => setDateOp(e.target.value)} />
        </Field>
        <Field label={t("bha.col.md")}>
          <input type="number" step="any" inputMode="decimal" value={mdOp} onChange={(e) => setMdOp(e.target.value)} />
        </Field>
        <Field label={t("bha.col.duration")}>
          <input
            type="number"
            step="any"
            inputMode="decimal"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
          />
        </Field>
        <Field label={t("bha.col.progress")}>
          <input
            type="number"
            step="any"
            inputMode="decimal"
            value={progress}
            onChange={(e) => setProgress(e.target.value)}
          />
        </Field>
        <Field label={t("bha.col.rop")}>
          <input type="number" step="any" inputMode="decimal" value={rop} onChange={(e) => setRop(e.target.value)} />
        </Field>
        <Field label={t("bha.col.code")}>
          <PicklistSelect
            categoryKey="activity_codes"
            value={codeKey}
            onChange={setCodeKey}
            placeholder={t("common.na")}
          />
        </Field>
      </div>
    </Modal>
  );
}

function BitOpRow({ op, locale }: { op: BitOpOut; locale: Locale }) {
  return (
    <tr data-owner="p">
      <td className="mono" style={{ whiteSpace: "nowrap" }}>{fmtDate(op.date_op, locale)}</td>
      <td className="num tab-nums">{fmtDepth(op.md_op, locale)}</td>
      <td className="num tab-nums">{op.op_duration ?? "—"}</td>
      <td className="num tab-nums">{fmtDepth(op.op_progress, locale)}</td>
      <td className="num tab-nums">{op.rop ?? "—"}</td>
      <td>
        <span className="mono">{op.activity_code_entry_key ?? "—"}</span>
      </td>
    </tr>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div className="kv">
      <span className="kv-k">{k}</span>
      <span className={`kv-v${mono ? " mono" : ""}`}>{v}</span>
    </div>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────

type TFn = (key: import("@i18n/messages").MessageKey) => string;

/** Map a mutation error to a user-facing string; 403 → operators-only note. */
function errorMessage(error: unknown, t: TFn, _locale: Locale): string {
  const status =
    error && typeof error === "object" && "status" in error
      ? (error as { status?: unknown }).status
      : undefined;
  if (status === 403) return t("bhaEdit.forbidden");
  return t("bhaEdit.saveFailed");
}

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return "";
  // Accept both ISO date and datetime; the <input type=date> wants YYYY-MM-DD.
  return iso.slice(0, 10);
}

function numToStr(n: number | null | undefined): string {
  return n == null ? "" : String(n);
}

function strToNum(s: string): number | null {
  const v = s.trim();
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function nullIfBlank(s: string): string | null {
  const v = s.trim();
  return v === "" ? null : v;
}

/** A server date string (ISO date or datetime) in the tenant locale; "—" when null. */
function fmtDate(iso: string | null | undefined, locale: Locale): string {
  if (!iso) return "—";
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}
